/**
 * Pocket Up — standalone voice-first PWA (route /pocket, no CRM layout).
 * Luxury fintech design · offline queue · biometric lock · quick categories · smart insights.
 * Data lives in the CRM finance backend (/api/finance/*).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, Square, Loader2, Home, History as HistoryIcon, Wallet, Activity, CheckCircle2, X, Download, Menu, ScanFace, ArrowRight, Quote } from 'lucide-react';
import api, { financeApi } from '../lib/api';
import { toast, Toaster } from 'sonner';
import { PocketLogin } from '../components/pocket/PocketLogin';
import { PocketLock } from '../components/pocket/PocketLock';
import { PocketBrand } from '../components/pocket/PocketLogo';
import { PocketHome, fmtMoney } from '../components/pocket/PocketHome';
import { PocketInsights } from '../components/pocket/PocketInsights';
import { PocketHistory, PocketWallet } from '../components/pocket/PocketViews';
import { QuickAddSheet, ManageCatsSheet } from '../components/pocket/QuickAddSheet';
import { PocketDrawer, SavingsView, ReserveView, PersonView, ChequesView, AICheckerView } from '../components/pocket/PocketTabs';
import { bioEnabled, disableBio, bioSupported, enableBio } from '../lib/pocketBio';
import { getQueue, pushQueue, flushQueue } from '../lib/pocketOffline';
import './Pocket.css';

const TOKEN_KEY = 'crm_token';
const USER_KEY = 'crm_user';

export default function Pocket() {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [locked, setLocked] = useState(() =>
    !!localStorage.getItem(USER_KEY) && bioEnabled() && sessionStorage.getItem('pocket_unlocked') !== '1');
  const [tab, setTab] = useState('home');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [offerBio, setOfferBio] = useState(false);
  const [wallets, setWallets] = useState(null);
  const [recent, setRecent] = useState([]);
  const [insights, setInsights] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cheques, setCheques] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [categories, setCategories] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(getQueue().length);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [notifState, setNotifState] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

  // voice + sheets
  const [phase, setPhase] = useState('idle'); // idle | recording | processing
  const [recSecs, setRecSecs] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const micStreamRef = useRef(null);
  const levelCtxRef = useRef(null);
  const levelRafRef = useRef(0);
  const peakRef = useRef(0);
  const [micLevel, setMicLevel] = useState(0);
  const cancelRef = useRef(false);
  const autoRecRef = useRef(false);
  const hiddenAtRef = useRef(0);
  const tabHist = useRef([]);

  // In-app tab navigation with a back stack (+ phone back button support)
  const goTab = useCallback((t) => {
    setTab((cur) => {
      if (t === cur) return cur;
      tabHist.current.push(cur);
      try { window.history.pushState({ pk: 1 }, ''); } catch (_) {}
      return t;
    });
  }, []);
  const goBack = useCallback(() => {
    const prev = tabHist.current.pop();
    setTab(prev || 'home');
  }, []);
  useEffect(() => {
    const onPop = () => { if (tabHist.current.length) goBack(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [goBack]);
  const [confirmItems, setConfirmItems] = useState(null);
  const [quickCat, setQuickCat] = useState(null);
  const [manageCats, setManageCats] = useState(false);

  const load = useCallback(async () => {
    const [w, t, ins, cats, sum, chq, led] = await Promise.allSettled([
      financeApi.walletBalances(),
      financeApi.transactions({ limit: 200 }),
      financeApi.pocketInsights(),
      financeApi.pocketCategories(),
      financeApi.summary(),
      financeApi.cheques(),
      financeApi.reserveLedger(),
    ]);
    if (w.status === 'fulfilled') setWallets(w.value.data);
    if (t.status === 'fulfilled') setRecent(t.value.data || []);
    if (ins.status === 'fulfilled') setInsights(ins.value.data);
    if (cats.status === 'fulfilled') setCategories(cats.value.data.categories || []);
    if (sum.status === 'fulfilled') setSummary(sum.value.data);
    if (chq.status === 'fulfilled') setCheques(chq.value.data || []);
    if (led.status === 'fulfilled') setLedger(led.value.data);
  }, []);

  useEffect(() => {
    if (!user || locked) return;
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) api.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    load();
  }, [user, locked, load]);

  // Offline / online tracking + auto-sync
  const syncQueue = useCallback(async (silent = false) => {
    const { sent, remaining } = await flushQueue(financeApi);
    setQueueCount(remaining);
    if (sent > 0) {
      toast.success(`📡 تم رفع ${sent} حركة كانت محفوظة offline ✓`);
      load();
    } else if (!silent && remaining > 0) {
      toast.error('لسا ما في نت — رح نحاول تلقائيًا');
    }
  }, [load]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncQueue(true); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (navigator.onLine && getQueue().length > 0 && user && !locked) syncQueue(true);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, [syncQueue, user, locked]);

  // Weekly insight local notification (once per week)
  useEffect(() => {
    if (!insights) return;
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted'
        && localStorage.getItem('pocket_insight_wk') !== insights.week_key) {
        const d = insights.delta_pct;
        const body = d == null
          ? `صرفت هالأسبوع ${fmtMoney(insights.week_total)}`
          : `صرفت هالأسبوع ${fmtMoney(insights.week_total)} — ${d >= 0 ? 'أعلى' : 'أقل'} من متوسطك بـ ${Math.abs(d)}%`;
        new Notification('Pocket Up — ملخص الأسبوع', { body, icon: '/pocket-icon-192.png' });
        localStorage.setItem('pocket_insight_wk', insights.week_key);
      }
    } catch { /* noop */ }
  }, [insights]);

  const enableNotifs = async () => {
    try {
      const p = await Notification.requestPermission();
      setNotifState(p);
      if (p === 'granted') {
        toast.success('تم تفعيل الإشعارات ✓');
        localStorage.removeItem('pocket_insight_wk');
      }
    } catch { toast.error('جهازك لا يدعم الإشعارات'); }
  };

  // Manifest swap + theme color + apple meta + service worker (offline shell)
  // NOTE: iOS Safari reads these tags at the moment "Add to Home Screen" is tapped.
  // We swap manifest AND apple-mobile-web-app-title AND apple-touch-icon so the
  // installed icon opens /pocket (not the main CRM).
  useEffect(() => {
    const setOrCreate = (selector, attrs) => {
      let el = document.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(selector.split('[')[0]);
        document.head.appendChild(el);
      }
      const prev = {};
      Object.entries(attrs).forEach(([k, v]) => {
        prev[k] = el.getAttribute(k);
        el.setAttribute(k, v);
      });
      return { el, prev, created };
    };

    const manifest = setOrCreate('link[rel="manifest"]', { rel: 'manifest', href: '/pocket-manifest.json' });
    const theme = setOrCreate('meta[name="theme-color"]', { name: 'theme-color', content: '#0B0A08' });
    const appleTitle = setOrCreate('meta[name="apple-mobile-web-app-title"]', { name: 'apple-mobile-web-app-title', content: 'Pocket Up' });
    const appleCapable = setOrCreate('meta[name="apple-mobile-web-app-capable"]', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    const appleStatus = setOrCreate('meta[name="apple-mobile-web-app-status-bar-style"]', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    const appleIcon = setOrCreate('link[rel="apple-touch-icon"]', { rel: 'apple-touch-icon', href: '/pocket-icon-180.png' });

    // Update the tab title too (iOS uses this as fallback for the home screen label)
    const origDocTitle = document.title;
    document.title = 'Pocket Up';

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/pocket-sw.js', { scope: '/pocket' }).catch(() => {});
    }

    return () => {
      const restore = ({ el, prev, created }) => {
        if (created) { el.remove(); return; }
        Object.entries(prev).forEach(([k, v]) => {
          if (v === null) el.removeAttribute(k); else el.setAttribute(k, v);
        });
      };
      restore(manifest);
      restore(theme);
      restore(appleTitle);
      restore(appleCapable);
      restore(appleStatus);
      restore(appleIcon);
      document.title = origDocTitle;
    };
  }, []);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const doInstall = async () => {
    if (!installPromptEvt) {
      toast('على iPhone: افتح Share ⤴ ثم "Add to Home Screen"', { duration: 6000 });
      return;
    }
    installPromptEvt.prompt();
    await installPromptEvt.userChoice;
    setInstallPromptEvt(null);
  };

  // ── VOICE RECORDING ─────────────────────────────────────────────────
  // Fresh mic stream on EVERY recording (a cached stream goes silent/muted after
  // the PWA is backgrounded → Whisper would hallucinate the same text each time).
  const pickMime = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus'].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
  const stopMic = () => {
    cancelAnimationFrame(levelRafRef.current);
    try { levelCtxRef.current?.close(); } catch { /* noop */ }
    levelCtxRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
  };
  const startRec = async () => {
    try {
      stopMic();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      micStreamRef.current = stream;
      const mime = pickMime();
      const ext = mime.includes('mp4') || mime.includes('aac') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      // live level meter + silence guard
      peakRef.current = 0; setMicLevel(0);
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx(); levelCtxRef.current = ctx; ctx.resume?.().catch(() => {});
        const an = ctx.createAnalyser(); an.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(an);
        const buf = new Uint8Array(512);
        const loop = () => {
          an.getByteTimeDomainData(buf);
          let sum = 0; for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          const lvl = Math.min(100, Math.round(Math.sqrt(sum / buf.length) * 300));
          peakRef.current = Math.max(peakRef.current, lvl); setMicLevel(lvl);
          levelRafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch { /* meter optional */ }
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        clearInterval(timerRef.current);
        const peak = peakRef.current;
        const muted = stream.getAudioTracks().some((t) => t.muted);
        const meterOk = levelCtxRef.current?.state === 'running';
        stopMic();
        if (cancelRef.current) { cancelRef.current = false; setPhase('idle'); return; }
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        if (blob.size < 800) { toast.error('التسجيل قصير'); setPhase('idle'); return; }
        if (muted || (meterOk && peak < 3)) { toast.error('ما سمعت صوتك — المايك صامت أو مكتوم. تأكد من إذن المايكروفون وحاول مجددًا', { duration: 6000 }); setPhase('idle'); return; }
        if (!navigator.onLine) { toast.error('التحليل الصوتي بحاجة نت — استخدم التسجيل السريع بالفئات'); setPhase('idle'); return; }
        setPhase('processing');
        setTranscript(null);
        const fd = new FormData();
        fd.append('audio', blob, `pocket.${ext}`);
        try {
          // Stage 1: transcribe → show "what I heard" immediately
          const tr = await financeApi.voiceTranscribe(fd);
          const heard = (tr.data.transcript || '').trim();
          setTranscript(heard);
          // Stage 2: parse the transcript into entries
          const r = await financeApi.voiceParse(heard);
          // executed voice actions (delete/edit on existing transactions)
          const actions = Array.isArray(r.data.actions) ? r.data.actions : [];
          actions.forEach((a) => {
            if (a.ok && a.action === 'delete' && a.tx_id) {
              toast.success(`🗑️ تم حذف: ${a.label} ✓`, {
                duration: 5000,
                action: {
                  label: 'تراجع ↩',
                  onClick: async () => {
                    try {
                      await financeApi.restoreTransaction(a.tx_id);
                      toast.success('رجعت الحركة مثل ما كانت ✓');
                      load();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'فشل الاسترجاع'); }
                  },
                },
              });
            } else if (a.ok) {
              toast.success(`✏️ تم تعديل: ${a.label} ✓`, { duration: 6000 });
            } else {
              toast.error(`${a.action === 'delete' ? 'الحذف فشل' : 'التعديل فشل'}${a.label ? ` (${a.label})` : ''} — ${a.error || ''}`, { duration: 6000 });
            }
          });
          if (actions.some((a) => a.ok)) load();
          const items = Array.isArray(r.data.items) ? r.data.items : [r.data.draft].filter(Boolean);
          if (items.length === 0) {
            if (actions.length === 0) toast.error('ما فهمت — حاول مجددًا');
            setPhase('idle');
            return;
          }
          setConfirmItems(items.map((it) => ({
            ...it,
            wallet: it.wallet || (it.type === 'personal' ? 'personal' : 'reserve'),
            _include: true,
          })));
          setPhase('idle');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'فشل التحليل', { duration: 6000 });
          setPhase('idle');
        }
      };
      rec.start();
      recRef.current = rec;
      setRecSecs(0);
      timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
      setPhase('recording');
    } catch {
      toast.error('لا يمكن الوصول للمايكروفون');
    }
  };
  const stopRec = () => { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); };
  const cancelRec = () => {
    cancelRef.current = true;
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    else setPhase('idle');
  };

  // ── AUTO-RECORD ON APP OPEN ─────────────────────────────────────────
  const phaseRef = useRef('idle');
  phaseRef.current = phase;
  const confirmRef = useRef(null);
  confirmRef.current = confirmItems;
  const canAutoRec = () => phaseRef.current === 'idle' && !confirmRef.current && !offerBioRef.current;
  const offerBioRef = useRef(false);
  offerBioRef.current = offerBio;

  useEffect(() => {
    if (!user || locked || offerBio || autoRecRef.current) return undefined;
    autoRecRef.current = true;
    const t = setTimeout(() => { if (canAutoRec()) startRec(); }, 700);
    return () => clearTimeout(t);
  }, [user, locked, offerBio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-arm when the app comes back from the background (PWA resume)
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) { hiddenAtRef.current = Date.now(); return; }
      if (!user || locked) return;
      if (Date.now() - hiddenAtRef.current > 10000 && canAutoRec()) startRec();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [user, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  const toQueueEntry = (it) => it.intent === 'cheque'
    ? { kind: 'cheque', payload: { payee: it.payee || it.category || 'غير محدد', amount: it.amount, currency: it.currency || 'USD', purpose: it.category || it.description || '', due_date: it.due_date, remind_days_before: 2, source: 'pocket' } }
    : { kind: 'tx', payload: { type: it.type || 'office', person: (it.type || 'office') === 'personal' ? (it.person || undefined) : undefined, amount: it.amount, currency: it.currency || 'USD', category: it.category || 'General', description: it.description || '', source: 'pocket', ...((it.type || 'office') === 'income' ? {} : { wallet: it.wallet || ((it.type || 'office') === 'office' ? 'reserve' : 'personal') }) } };

  const commit = async () => {
    const kept = (confirmItems || []).filter((it) => it._include && it.amount > 0);
    if (!kept.length) { toast.error('لا يوجد شي للحفظ'); return; }
    if (!navigator.onLine) {
      kept.forEach((it) => pushQueue(toQueueEntry(it)));
      setQueueCount(getQueue().length);
      setConfirmItems(null);
      toast(`📡 لا يوجد نت — حُفظت ${kept.length} حركة محليًا وسترفع تلقائيًا`, { duration: 5000 });
      return;
    }
    let ok = 0;
    for (const it of kept) {
      const entry = toQueueEntry(it);
      try {
        if (entry.kind === 'cheque') await financeApi.addCheque(entry.payload);
        else await financeApi.addTransaction(entry.payload);
        ok++;
      } catch (err) {
        if (!err?.response) { pushQueue(entry); setQueueCount(getQueue().length); }
      }
    }
    toast.success(`تم حفظ ${ok} ${ok === 1 ? 'قيد' : 'قيود'} ✓`);
    setConfirmItems(null);
    load();
  };

  // Quick category save (offline-aware)
  const quickSave = async (payload) => {
    if (!navigator.onLine) {
      pushQueue({ kind: 'tx', payload });
      setQueueCount(getQueue().length);
      setQuickCat(null);
      toast(`📡 حُفظ «${payload.category}» محليًا — يرفع لما يرجع النت`, { duration: 4500 });
      return;
    }
    try {
      await financeApi.addTransaction(payload);
      toast.success(`تم حفظ ${payload.category} — ${fmtMoney(payload.amount)} ✓`);
      setQuickCat(null);
      load();
    } catch (err) {
      if (!err?.response) {
        pushQueue({ kind: 'tx', payload });
        setQueueCount(getQueue().length);
        setQuickCat(null);
        toast('📡 مشكلة نت — حُفظت محليًا');
      } else {
        toast.error(err?.response?.data?.detail || 'فشل الحفظ');
      }
    }
  };

  useEffect(() => {
    const onReload = () => load();
    window.addEventListener('pocket-reload', onReload);
    return () => window.removeEventListener('pocket-reload', onReload);
  }, [load]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem('pocket_unlocked');
    setUser(null);
    setLocked(false);
  };

  // ── RENDER ──────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="pk-root" dir="rtl">
        <PocketLogin onLoggedIn={(u) => {
          setUser(u);
          sessionStorage.setItem('pocket_unlocked', '1');
          if (bioSupported() && !bioEnabled() && localStorage.getItem('pocket_bio_offer_done') !== '1') setOfferBio(true);
        }} />
        <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: '#1B1814', border: '1px solid rgba(245,158,11,0.3)', color: '#FAF8F5', fontFamily: 'Fustat, sans-serif' } }} />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="pk-root" dir="rtl">
        <PocketLock
          user={user}
          onUnlocked={() => { sessionStorage.setItem('pocket_unlocked', '1'); setLocked(false); }}
          onUsePassword={() => { disableBio(); logout(); }}
        />
        <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: '#1B1814', border: '1px solid rgba(245,158,11,0.3)', color: '#FAF8F5', fontFamily: 'Fustat, sans-serif' } }} />
      </div>
    );
  }

  return (
    <div className="pk-root" dir="rtl">
      {/* unified top navbar: menu/back (right) · brand (center) · install (left) */}
      <div className="pk-navbar" data-testid="pocket-navbar">
        <div className="pk-nav-side">
          <button onClick={() => setDrawerOpen(true)} data-testid="pocket-menu-btn" className="pk-navbtn">
            <Menu size={19} strokeWidth={2.2} />
          </button>
          {tab !== 'home' && (
            <button onClick={goBack} data-testid="pocket-back-btn" className="pk-navbtn" title="رجوع">
              <ArrowRight size={19} strokeWidth={2.3} />
            </button>
          )}
        </div>
        <PocketBrand />
        <div className="pk-nav-side end">
          <button onClick={doInstall} data-testid="pocket-install-btn" className="pk-install-pill">
            <Download size={13} /> تثبيت
          </button>
        </div>
      </div>

      <PocketDrawer open={drawerOpen} tab={tab} onPick={goTab} onClose={() => setDrawerOpen(false)} user={user} />

      {tab === 'home' && (
        <PocketHome user={user} wallets={wallets} recent={recent} insights={insights}
          categories={categories} online={online} queueCount={queueCount}
          onQuickTap={setQuickCat} onManageCats={() => setManageCats(true)} onGoInsights={() => goTab('insights')} />
      )}
      {tab === 'insights' && <PocketInsights insights={insights} notifState={notifState} onEnableNotifs={enableNotifs} />}
      {tab === 'history' && <PocketHistory recent={recent} />}
      {tab === 'savings' && <SavingsView summary={summary} />}
      {tab === 'reserve' && <ReserveView wallets={wallets} ledger={ledger} />}
      {tab === 'person_ismail' && <PersonView label="إسماعيل" keys={['اسماعيل', 'ismail', 'esmail']} recent={recent} summary={summary} />}
      {tab === 'person_rafat' && <PersonView label="رأفت" keys={['رافت', 'رأفت', 'rafat', 'raafat']} recent={recent} summary={summary} />}
      {tab === 'cheques' && <ChequesView cheques={cheques} onChanged={load} />}
      {tab === 'ai' && <AICheckerView summary={summary} />}
      {tab === 'wallet' && (
        <PocketWallet wallets={wallets} user={user} queueCount={queueCount}
          onSyncNow={() => syncQueue(false)} onLogout={logout} />
      )}

      {/* bottom dock: 4 tabs + center mic FAB */}
      <div className="pk-dock" data-testid="pocket-dock">
        <button className={`pk-nav-item ${tab === 'home' ? 'active' : ''}`} onClick={() => goTab('home')} data-testid="pocket-nav-home">
          <span className="nav-ic"><Home size={21} strokeWidth={tab === 'home' ? 2.2 : 1.7} /></span> الرئيسية
        </button>
        <button className={`pk-nav-item ${tab === 'insights' ? 'active' : ''}`} onClick={() => goTab('insights')} data-testid="pocket-nav-insights">
          <span className="nav-ic"><Activity size={21} strokeWidth={tab === 'insights' ? 2.2 : 1.7} /></span> تحليلات
        </button>
        <div className="pk-fab-wrap">
          <button className="pk-fab" onClick={phase === 'idle' ? startRec : undefined} data-testid="pocket-mic-btn">
            <span className="pk-fab-inner">
              {phase === 'processing' ? <Loader2 size={28} className="animate-spin" /> : <Mic size={28} strokeWidth={2.2} />}
            </span>
            {queueCount > 0 && <span className="pk-badge-q" data-testid="pocket-queue-badge">{queueCount}</span>}
          </button>
        </div>
        <button className={`pk-nav-item ${tab === 'history' ? 'active' : ''}`} onClick={() => goTab('history')} data-testid="pocket-nav-history">
          <span className="nav-ic"><HistoryIcon size={21} strokeWidth={tab === 'history' ? 2.2 : 1.7} /></span> السجل
        </button>
        <button className={`pk-nav-item ${tab === 'wallet' ? 'active' : ''}`} onClick={() => goTab('wallet')} data-testid="pocket-nav-wallet">
          <span className="nav-ic"><Wallet size={21} strokeWidth={tab === 'wallet' ? 2.2 : 1.7} /></span> المحفظة
        </button>
      </div>

      {/* recording overlay */}
      {phase === 'recording' && (
        <div className="pk-rec-overlay" data-testid="pocket-recording-overlay">
          <button className="pk-rec-x" onClick={cancelRec} data-testid="pocket-cancel-rec" title="إلغاء التسجيل">
            <X size={26} strokeWidth={2.6} />
          </button>
          <div className="pk-rec-rings">
            <div className="pk-ring" /><div className="pk-ring" /><div className="pk-ring" />
            <button className="pk-rec-core" onClick={stopRec} data-testid="pocket-stop-rec">
              <Square size={38} fill="currentColor" />
            </button>
          </div>
          <div className="pk-wave" data-testid="pocket-mic-wave" data-level={micLevel}>
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} style={{ animationDelay: `${(i % 7) * 0.11}s`, height: `${Math.max(6, Math.min(44, (micLevel * (0.5 + ((i * 7) % 5) / 6)) * 0.9 + 6))}px`, animation: 'none' }} />
            ))}
          </div>
          <div className="pk-rec-level" data-testid="pocket-mic-level">
            <span style={{ width: `${micLevel}%` }} />
          </div>
          {micLevel < 3 && recSecs >= 2 && levelCtxRef.current?.state === 'running' && <div className="pk-rec-hint" style={{ color: '#e11d48', fontWeight: 700 }} data-testid="pocket-mic-silent">⚠️ ما بسمعك — تأكد إن المايك مش مكتوم</div>}
          <div className="pk-rec-timer" dir="ltr">{String(Math.floor(recSecs / 60)).padStart(2, '0')}:{String(recSecs % 60).padStart(2, '0')}</div>
          <div className="pk-rec-hint">احكِ الآن… اضغط المربع لما تخلص — أو X للإلغاء</div>
        </div>
      )}
      {phase === 'processing' && (
        <div className="pk-rec-overlay" data-testid="pocket-processing-overlay">
          <div className="pk-proc-reticle">
            <span className="spin"><Loader2 size={34} className="animate-spin" /></span>
          </div>
          <div className="pk-rec-timer pk-latin" style={{ fontSize: 15, marginTop: 24, letterSpacing: '0.2em' }}>
            {transcript ? 'AI ANALYSIS' : 'LISTENING BACK'}
          </div>
          <div className="pk-rec-hint" aria-live="polite">
            {transcript ? 'الذكاء الاصطناعي يحوّل كلامك لقيود مالية…' : 'جارٍ تحويل صوتك إلى نص…'}
          </div>
          <div className="pk-transcript" data-testid="pocket-transcript-pill">
            <div className="lbl"><Quote size={11} /> سمعتك تقول</div>
            <div className={`txt ${transcript ? '' : 'dim'}`} dir="auto" data-testid="pocket-transcript-text">
              {transcript || 'أستمع للتسجيل'}
            </div>
          </div>
        </div>
      )}

      {/* voice confirm sheet */}
      {confirmItems && (
        <>
          <div className="pk-backdrop" onClick={() => setConfirmItems(null)} />
          <div className="pk-sheet" data-testid="pocket-confirm-sheet">
            <div className="pk-grip" />
            <div className="pk-sheet-title">راجع القيود</div>
            <div className="pk-sheet-sub">فهمت {confirmItems.length} {confirmItems.length === 1 ? 'قيد' : 'قيود'} — عدّل المحفظة أو استبعد ما لا تريده</div>
            {transcript && (
              <div className="pk-transcript" style={{ marginTop: 14, maxWidth: 'none' }} data-testid="pocket-confirm-transcript">
                <div className="lbl"><Quote size={11} /> سمعتك تقول</div>
                <div className="txt" dir="auto" style={{ fontSize: 14 }}>{transcript}</div>
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              {confirmItems.map((it, idx) => (
                <div key={idx} className="pk-card" style={{ padding: 13, marginBottom: 9, opacity: it._include ? 1 : 0.35 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 17, fontWeight: 800 }} className="pk-num">
                        <span dir="ltr" className="pk-gold-text">{fmtMoney(it.amount)}</span>
                        <span style={{ color: 'var(--pk-mut)', fontSize: 11, fontWeight: 500, marginInlineStart: 6 }}>{it.currency}{it.intent === 'cheque' ? ' · شيك' : ''}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--pk-text-2)', marginTop: 4 }} dir="auto">{it.category || it.description || '—'}</div>
                      {it.intent !== 'cheque' && it.type !== 'income' && (
                        <div className="pk-seg" style={{ marginTop: 9 }}>
                          <button className={it.wallet === 'personal' ? 'on' : ''}
                            onClick={() => setConfirmItems((arr) => arr.map((x, i) => i === idx ? { ...x, wallet: 'personal' } : x))}>Personal</button>
                          <button className={it.wallet === 'reserve' ? 'on mag' : ''}
                            onClick={() => setConfirmItems((arr) => arr.map((x, i) => i === idx ? { ...x, wallet: 'reserve' } : x))}>Reserve</button>
                        </div>
                      )}
                    </div>
                    <button style={{ background: 'none', border: 'none', color: 'var(--pk-mut)', padding: 4, cursor: 'pointer' }}
                      onClick={() => setConfirmItems((arr) => arr.map((x, i) => i === idx ? { ...x, _include: !x._include } : x))}>
                      {it._include ? <X size={16} /> : <CheckCircle2 size={16} style={{ color: 'var(--pk-green)' }} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button className="pk-btn" onClick={commit} data-testid="pocket-confirm-save">
              احفظ الكل ({confirmItems.filter((x) => x._include).length})
            </button>
            <button className="pk-btn ghost" onClick={() => setConfirmItems(null)}>إلغاء</button>
          </div>
        </>
      )}

      {quickCat && <QuickAddSheet category={quickCat} user={user} onClose={() => setQuickCat(null)} onSave={quickSave} />}
      {manageCats && <ManageCatsSheet categories={categories} onClose={() => setManageCats(false)} onSaved={setCategories} />}

      {/* Face ID auto-offer right after login */}
      {offerBio && (
        <>
          <div className="pk-backdrop" onClick={() => { localStorage.setItem('pocket_bio_offer_done', '1'); setOfferBio(false); }} />
          <div className="pk-sheet" data-testid="pocket-bio-offer-sheet">
            <div className="pk-grip" />
            <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
              <ScanFace size={44} style={{ color: 'var(--pk-gold)' }} />
            </div>
            <div className="pk-sheet-title" style={{ textAlign: 'center' }}>فعّل الدخول بـ Face ID</div>
            <div className="pk-sheet-sub" style={{ textAlign: 'center' }}>
              من هلق ورايح ما تحتاج username ولا password — كل ما تفتح التطبيق بيطلب وجهك مباشرة ويفتح
            </div>
            <button className="pk-btn" style={{ marginTop: 16 }} data-testid="pocket-bio-offer-enable"
              onClick={async () => {
                try {
                  await enableBio(user);
                  localStorage.setItem('pocket_bio_offer_done', '1');
                  sessionStorage.setItem('pocket_unlocked', '1');
                  setOfferBio(false);
                  toast.success('تم تفعيل Face ID ✓ — من الآن التطبيق يفتح بوجهك مباشرة بدون أي كبسة');
                } catch { toast.error('ما قدرت أفعّل — جهازك لازم يدعم Face ID / Touch ID'); }
              }}>
              تفعيل Face ID الآن
            </button>
            <button className="pk-btn ghost" onClick={() => { localStorage.setItem('pocket_bio_offer_done', '1'); setOfferBio(false); }} data-testid="pocket-bio-offer-later">لاحقًا</button>
          </div>
        </>
      )}

      <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: '#1B1814', border: '1px solid rgba(245,158,11,0.3)', color: '#FAF8F5', fontFamily: 'Fustat, sans-serif' } }} />
    </div>
  );
}
